import { Injectable } from '@angular/core';
import {
  DocumentData,
  Firestore,
  Timestamp,
  collection,
  collectionData,
  doc,
  query,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  AttributeKey,
  AttributeKeys,
  VideoAttributeValue,
  VideoAttributesMapping,
} from '@yoimo/interfaces';
import { BehaviorSubject, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { StreamData } from '../home/channels-view/schedule-event/schedule-event.component';
import { UserData } from '../interfaces/user';
import { MediaConversionJob } from './master-channel.service';

export interface ReservedInputParam {
  arn: string;
  id: string;
  region: string;
  rtmpUrl?: string;
  srtUrl?: string;
  type?: string;
}

export type VideoPayload = Omit<VideoUploadData, 
    'error' | 'errorType' | 'errorsFixed' | 'row' | 'id' | 'clip' | 'targetRegion' | 'videoLocation' | 'livestreamDocId' | 'createdTime' | 'restrictedToExclude'>;

export interface BatchUpload {
  docId: string;
  channelId: string;
  eventId: string;
  createdBy: string;
  createdTime: Timestamp | Date;
  deleted?: boolean;
  deletedAt?: Timestamp | Date;
}

export interface VideoUploadData extends Omit<MediaConversionJob, 'restrictedTo'> {
  restrictedTo?: string[] | { exclude: string[] };
  restrictedToExclude?: string[];
  countryType?: 'include' | 'exclude';
  error?: string[] | null;
  errorType?: string[];
  errorsFixed?: string[];
  row?: number;
}

export interface BulkVideoUploadResponse {
  name: string;
  error?: string | string[];
}

export interface VideoData {
  row: number;
  name: string;
  description?: string;
  inputUrl: string;
  tags?: string[];
  eventId?: string;
  country: string;
  restrictedTo?: string[] | { exclude: string[] };
  restrictedToExclude?: string[];
  countryType?: 'include' | 'exclude';
  /** Set when sheet country_type was present but not include/exclude. */
  countryTypeInvalid?: boolean;
  /** Set when sheet country had invalid codes and a valid country type remains. */
  countryInvalid?: boolean;
  posterImage?: string;
  unlocked: boolean;
  published: boolean;
  errorType?: string[];
  attr0?: string;
  attr1?: string;
  attr2?: string;
  attr3?: string;
}

export interface Owner{
   data: UserData; ownerDocId: string; 
}

type OwnerWithEvents = { owner: string; hasEvents: boolean };
type StartEdit = {isEdit: boolean; canReupload?: boolean;}

@Injectable({
  providedIn: 'root',
})
export class BulkImportService {
  clubId = '';
  teamId = '';
  teamName = '';
  ownerDocList: Owner[];
  seasonTicketsList: { docId: string; name: string; }[] = [];
  csvData: StreamData[] = [];
  selectedEventsWithOwners: StreamData[];
  videosToBeUploaded: VideoUploadData[] | VideoData[];
  selectedOwner = '';
  selectedSeasonTicket?: {
    name: string;
    docId: string;
  };

  selectedEventsWithSeason: any;
  uploadingStream: boolean;
  uploadingVideos: boolean;
  eventsToBeUploaded: StreamData[];
  clubName = '';
  private readonly chosenOwner: BehaviorSubject<OwnerWithEvents>;
  private readonly startEdit: BehaviorSubject<StartEdit>;
  private readonly reviewBlockingErrors: BehaviorSubject<boolean>;
  shareSelectedOwner: Observable<OwnerWithEvents>;
  shareStartEditing: Observable<StartEdit>;
  shareReviewBlockingErrors: Observable<boolean>;
  private readonly callFunction = new Subject();
  private readonly callSeasonTable = new Subject();
  fileName: string;
  isNewChannel = false;
  channelId = '';
  emailRecipients: string[];
  videoErrorResponses: {    [k in string]: {
      errorType: string;
      errorMessages: string[];
    };
  } = {};
  errorResponses: {
    [k in string]: {
      errorType: string;
      errorMessages: string[];
    };
  } = {};
  eventSourceDocs: Record<string, string>[] = [];
  selectedEvent = '';
  attributeMappings: VideoAttributesMapping['mappings'] = {};
  reservedInput: ReservedInputParam[] = [];
  invalidReservedInput: boolean[] = [];
  csvVideoData: VideoData[] = [];
  selectedVideosWithEvent: VideoData[] = [];
  uploadingVideo = false;

  constructor(private readonly firestore: Firestore, private readonly functions: Functions) {
    this.clubId = '';
    this.teamId = '';
    this.teamName = '';
    this.selectedOwner = '';
    this.selectedEvent = '';
    this.ownerDocList = [];
    this.uploadingStream = false;
    this.uploadingVideos = false;
    this.eventsToBeUploaded = [];
    this.videosToBeUploaded = [];
    this.selectedEventsWithOwners = [];
    this.selectedEventsWithSeason = [];
    this.chosenOwner = new BehaviorSubject<OwnerWithEvents>({
      owner: this.selectedOwner,
      hasEvents: false,
    });
    this.startEdit = new BehaviorSubject<StartEdit>({ isEdit: false });
    this.reviewBlockingErrors = new BehaviorSubject<boolean>(false);
    this.shareSelectedOwner = this.chosenOwner.asObservable();
    this.shareStartEditing = this.startEdit.asObservable();
    this.shareReviewBlockingErrors = this.reviewBlockingErrors.asObservable();
    this.fileName = '';
    this.emailRecipients = [];
    this.setErrorResponses();
    this.setVideoErrorResponses();
  }

  getBulkImportsFromUploadCollection(channelId: string): Observable<DocumentData[] | BatchUpload[]> {
    const uploadsRef = collection(this.firestore, 'uploadBulkImports');
    const q = query(
      uploadsRef, 
      where('channelDocId', '==', channelId)
    );
    return collectionData(q, { idField: 'docId' }).pipe(
      catchError(error => {
        console.error('Error fetching bulk imports:', error);
        return of([]); 
      })
    );
  }

  async getBulkImportedVideos(bulkImportId: string): Promise<number> {
    const videosRef = collection(this.firestore, 'mediaConversionJobs');
    const q = query(videosRef, where('bulkImportId', '==', bulkImportId), where('status', '==', 'COMPLETE'));
    const videosData = await firstValueFrom(collectionData(q, { idField: 'docId' }));
    return videosData.length;
  } 

  showHeaderTooltip(event: MouseEvent, tip: HTMLElement): void {
    const icon = event.currentTarget as HTMLElement;
    const rect = icon.getBoundingClientRect();
    const tipWidth = 180;
  
    let left = rect.left + rect.width / 2 - tipWidth / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - tipWidth - 8));
  
    tip.style.top = `${rect.bottom + 10}px`;
    tip.style.left = `${left}px`;
    tip.style.visibility = 'visible';
    tip.style.opacity = '1';
  }
  
  hideHeaderTooltip(tip: HTMLElement): void {
    tip.style.visibility = 'hidden';
    tip.style.opacity = '0';
  }
  
  getAttrFilter(
    attributesFilter: any,
    key: AttributeKey
  ) {
    return attributesFilter?.[key] ?? { searchText: '', filterList: [] };
  }

  getAttrValues(
    attributes: VideoAttributesMapping['mappings'] | undefined, 
    key: AttributeKey
  ): VideoAttributeValue[] {
    return attributes?.[key]?.values ?? [];
  }

  getAttrLabel(
    attributes: VideoAttributesMapping['mappings'] | undefined, 
    key: AttributeKey
  ): string | undefined {
    return attributes?.[key]?.label;
  }

  getAttrSelectExpanded(
    expandedMap: { [key in AttributeKey]: boolean }[], 
    index: number, 
    key: AttributeKey
  ): boolean {
    return expandedMap?.[index]?.[key] ?? false;
  }

  async uploadVideosBulk(
    uploadData: { channelDocId: string; eventId: string; videos: VideoPayload[] }
  ): Promise<BulkVideoUploadResponse[]> {
    const bulkVideoUpload = httpsCallable <{ channelDocId: string; eventId: string; videos: VideoPayload[] }, BulkVideoUploadResponse[]>(
      this.functions, 
      'videos-uploadBulkImport'
    );
    const response = await bulkVideoUpload(uploadData);
    return response.data;
  }

  async deleteBulkImport(docId: string): Promise<void> {
    const docRef = doc(this.firestore, 'uploadBulkImports', docId);
    return updateDoc(docRef, {
      deleted: true,
      deletedAt: Timestamp.now()
    });
  }

  resetBulk() {
    this.clubId = '';
    this.teamId = '';
    this.teamName = '';
    this.selectedOwner = '';
    this.selectedEvent = '';
    this.uploadingStream = false;
    this.eventsToBeUploaded = [];
    this.selectedEventsWithOwners = [];
    this.selectedEventsWithSeason = [];
    this.csvData = [];
    this.selectedSeasonTicket = undefined;
    this.chosenOwner.next({ owner: '', hasEvents: false });
    this.startEdit.next({ isEdit: false });
    this.reviewBlockingErrors.next(false);
    this.fileName = '';
    this.emailRecipients = [];
    this.invalidReservedInput = [];
    this.resetVideoData();
  }

  resetVideoData() {
    this.csvVideoData = [];
    this.selectedVideosWithEvent = [];
    this.uploadingVideo = false;
    this.videosToBeUploaded = [];
  }

  resetSelectedValues() {
    this.selectedOwner = '';
    this.selectedSeasonTicket = undefined;
    this.selectedEventsWithOwners = [];
    this.selectedEventsWithSeason = [];
    this.csvData = [];
    this.chosenOwner.next({ owner: '', hasEvents: false });
    this.startEdit.next({ isEdit: false });
    this.reviewBlockingErrors.next(false);
  }

  setChosenOwner(ownerWithEvents: OwnerWithEvents) {
    this.chosenOwner.next(ownerWithEvents);
  }
  setStartEdit(edit: StartEdit) {
    this.startEdit.next(edit);
  }

  setReviewBlockingErrors(hasBlockingErrors: boolean) {
    this.reviewBlockingErrors.next(hasBlockingErrors);
  }

  sendForwardEvent() {
    this.callFunction.next(null);
  }

  getForwardEvent() {
    return this.callFunction.asObservable();
  }

  sendPreviousEvent() {
    this.selectedEvent = '';
    this.callSeasonTable.next(null);
  }

  getPreviousEvent() {
    return this.callSeasonTable.asObservable();
  }

  getBulkImportData(teamDocId: string) {
    const bulkImportsRef = collection(this.firestore, 'livestreamBulkImports');
    const q = query(bulkImportsRef, where('teamDocId', '==', teamDocId));
    return collectionData(q, { idField: 'docId' });
  }

  getAttributeValueLabel(
    attribute: AttributeKey,
    valueName?: string
  ): string | undefined {
    const value = this.attributeMappings?.[attribute]?.values.find(
      (attributeValue: VideoAttributeValue) => attributeValue.name == valueName
    );
    return value?.label ? `${value.name}(${value?.label})` : value?.name;
  }

  getRtmpUrl(id?: string): string {
    const value = this.reservedInput.find(
      (item: ReservedInputParam) => item.id == id
    );
    return value?.rtmpUrl ? value?.rtmpUrl : '';
  }

  getSrtUrl(id?: string): string {
    const value = this.reservedInput.find(
      (item: ReservedInputParam) => item.id == id
    );
    return value?.srtUrl ? value?.srtUrl : '';
  }

  setVideoErrorResponses() {
    this.videoErrorResponses = {
      name: {
        errorType: 'Video name is required',
        errorMessages: ['name is invalid', 'invalid name']
      },
      description: {
        errorType: 'Description is invalid',
        errorMessages: ['description is invalid', 'invalid description']
      },
      inputUrl: {
        errorType: 'Valid input URL is required',
        errorMessages: ['inputurl is invalid', 'inputurl is not a valid url', 'url is invalid', 'inputurl is not a valid video url']
      },
      duration: {
        errorType: 'Valid duration is required',
        errorMessages: ['duration is invalid', 'duration must be at least 1 second', 'duration must be less than 90 seconds for shorts']
      },
      type: {
        errorType: 'Video type must be UPLOAD or SHORT',
        errorMessages: ['type is invalid', 'invalid type']
      },
      tags: {
        errorType: 'Tags cannot be empty',
        errorMessages: ['tags is invalid', 'tags cannot be empty if provided']
      },
      posterImage: {
        errorType: 'Poster image must be a valid URL',
        errorMessages: ['posterimage is not a valid url']
      },
      attr0: {
        errorType: 'The attribute 0 or its value has been modified or removed',
        errorMessages: ['invalid attribute: attr0']
      },
      attr1: {
        errorType: 'The attribute 1 or its value has been modified or removed',
        errorMessages: ['invalid attribute: attr1']
      },
      attr2: {
        errorType: 'The attribute 2 or its value has been modified or removed',
        errorMessages: ['invalid attribute: attr2']
      },
      attr3: {
        errorType: 'The attribute 3 or its value has been modified or removed',
        errorMessages: ['invalid attribute: attr3']
      }
    };
  }

  setErrorResponses() {
    this.errorResponses = {
      name: {
        errorType: 'Name is invalid',
        errorMessages: ['name is invalid', 'invalid name'],
      },
      description: {
        errorType: 'Description is invalid',
        errorMessages: ['description is invalid', 'invalid description'],
      },
      date: {
        errorType: 'Date is invalid',
        errorMessages: [
          'scheduled start date should be in the future',
          'startdate is invalid',
          'date is invalid',
          'invalid date',
          'invalid startdate',
        ],
      },
      timezone: {
        errorType: 'Timezone is invalid',
        errorMessages: ['timezone is invalid'],
      },
      time: {
        errorType: 'Time is invalid',
        errorMessages: [
          'scheduled start time should be in the future',
          'invalid time',
          'invalid startTime',
          'start time is invalid',
          'starttime is invalid',
          'stream limit error',
        ],
      },
      duration: {
        errorType: 'Duration is invalid (It should be between 5 and 720 mins)',
        errorMessages: [
          'invalid duration',
          'duration is invalid',
          'scheduled stop time should be greater than scheduled start time',
          'duration should be between 5 and 720 minutes',
        ],
      },
      price: {
        errorType: 'Price is invalid',
        errorMessages: ['price is invalid', 'invalid price'],
      },
      vod: {
        errorType: 'Vodprice is invalid',
        errorMessages: ['vodprice is invalid', 'invalid vodprice'],
      },
      streamingMethod: {
        errorType: 'Streaming method is invalid',
        errorMessages: ['streamingmethod is invalid', 'sportydocid is invalid'],
      },
      reservedInput: {
        errorType:
          `Conflicting streams detected. The reserved input URL is already in use by another stream.
           Please choose a different start time / duration and try again`,
        errorMessages: ['reserved input conflict'],
      },
      tags: {
        errorType: 'Tags is invalid',
        errorMessages: ['tags is invalid'],
      },
      country: {
        errorType: "Country cannot be included and excluded at the same time",
        errorMessages: [
          "country cannot be included and excluded at the same time",
          "country cannot be included and excluded",
          "invalid country",
        ],
      },
      ...Object.assign(
        {},
        ...AttributeKeys.map((attribute) => ({
          [attribute]: {
            errorType: `The attribute ${attribute.slice(
              -1
            )} or its value has been modified or removed. You can proceed with Reupload.`,
            errorMessages: [`invalid attr ${attribute.slice(-1)}`],
          },
        }))
      ),
    };
  }
}
